import * as d3 from 'd3';
import { ChartDataArray } from '../data/ChartDataArray';
import { ChartDataComparisonType } from '../data/ChartDataComparisonType';
import { ChartDataType } from '../data/ChartDataType';
import { DataRenderer } from './DataRenderer';
import { ChartData } from '../data/ChartData';
import { ChartRenderer } from './ChartRenderer';

export class LineRenderer extends DataRenderer {
  private className?: string;

  constructor(
    private gElement: d3.Selection<SVGGElement, unknown, null, undefined>,
    private data: ChartDataArray,
    private index?: number,
    public secondaryAxis?: boolean,
    public removeOuterSpacing?: boolean
  ) {
    super();

    if (data.type === ChartDataType.primary && data.comparisonType === ChartDataComparisonType.plan) {
      this.className = 'plan-serie';
    } else if (data.type === ChartDataType.secondary && data.comparisonType === ChartDataComparisonType.plan) {
      this.className = 'plan-serie-secondary';
    } else if (data.type === ChartDataType.primary) {
      this.className = !data.isComparison ? 'main-serie' : 'comparison-serie';
    } else if (data.type === ChartDataType.secondary) {
      this.className = !data.isComparison ? 'main-serie-secondary' : 'comparison-serie-secondary';
    } else if (data.type === ChartDataType.breakdown) {
      this.className = 'parallel-serie';
    }

    if (data.dashed) {
      this.className = `${this.className} dashed`;
    }
  }

  public render(x: d3.ScaleBand<string>, y: d3.ScaleLinear<number, number>, limit?: number) {
    const isWithoutIndex = !this.index && this.index !== 0;
    const containers = this.gElement
      .selectAll(this.getClassSelector())
      .data([this.data.getData(limit)])
      .enter()
      .append('g')
      .attr('class', this.className + (isWithoutIndex ? '' : ` ${this.className}-${this.index}`));

    // TODO: Remove duplicate code
    if (x.domain().length === 1) {
      // add a short line in case there is only 1 data point available
      containers
        .append('path')
        .attr('fill', 'none')
        .attr('class', () => (isWithoutIndex ? 'line' : `line chart-stroke-color-${this.index}`))
        .style('stroke', () => this.data.color || '')
        .attr('d', (d: ChartData[]) => {
          const fixY = y(d[0].value!);
          const fixX = x(d[0].label!)!;
          const lineData: { xVal: number }[] = [
            {
              xVal: fixX - 6,
            },
            {
              xVal: fixX + 6,
            },
          ];
          return d3
            .line<{ xVal: number }>()
            .x(
              (dataEle: { xVal: number }, i) =>
                dataEle.xVal + ChartRenderer.getBandSpacing(x, i, !!this.removeOuterSpacing)
            )
            .y(() => fixY)(lineData);
        });
    } else {
      containers
        .append('path')
        .attr('fill', 'none')
        .attr('class', () => (isWithoutIndex ? 'line ' : `line chart-stroke-color-${this.index}`))
        .style('stroke', () => this.data.color || '')
        .attr(
          'd',
          d3
            .line<ChartData>()
            .x((d: ChartData, i) => x(d.label!)! + ChartRenderer.getBandSpacing(x, i, !!this.removeOuterSpacing))
            .y((d: ChartData) => y(d.value!))
        );

      if (this.data.type === ChartDataType.secondary) {
        containers.style('stroke-width', 2);
      }
    }
  }

  private getClassSelector() {
    const isWithoutIndex = !this.index && this.index !== 0;
    return `.${this.className}${isWithoutIndex ? '' : `.${this.className}-${this.index}`}`;
  }

  public renderGuides(x: d3.ScaleBand<string>, y: d3.ScaleLinear<number, number>, limit?: number) {
    const guideContainer = this.gElement
      .select(this.getClassSelector())
      .selectAll('dot')
      .data(this.data.getData(limit))
      .enter()
      .append('g')
      .attr('class', 'guide')
      .style('opacity', 0)
      .style('transition', 'opacity 0.2s ease');
    guideContainer
      .append('circle')
      .attr('class', 'dot')
      .attr('r', 3.5)
      .style('fill', () => this.data.color || '')
      .attr('cx', (d, i) => x(d.label!)! + ChartRenderer.getBandSpacing(x, i, !!this.removeOuterSpacing))
      .attr('cy', d => y(d.value!));
  }

  public switchHighlight(index: number, highlight: boolean) {
    const targetNodes = this.gElement.select(this.getClassSelector()).selectAll('.guide').nodes() as HTMLElement[];

    targetNodes.forEach(node => {
      node.classList.remove('show');
      node.style.opacity = '0';
    });
    if (highlight) {
      if (targetNodes.length === 0) {
        return;
      }
      targetNodes[index].classList.add('show');
      targetNodes[index].style.opacity = '1';
    }
  }
}
